<script setup lang="ts">
// Portal chrome; colours come from the brand theme.
defineProps<{
  logoUrl: string
  brandName: string
  context: string
  user: { displayName: string, email: string } | null
  signOutHref: string
}>()
</script>

<template>
  <aside
    class="sidebar"
    data-testid="portal-sidebar"
  >
    <div class="sidebar__logo">
      <img
        :src="logoUrl"
        :alt="brandName"
      >
    </div>
    <p class="sidebar__context">
      {{ context }}
    </p>
    <div class="sidebar__divider" />
    <div class="sidebar__body">
      <slot />
    </div>
    <footer
      v-if="user"
      class="sidebar__foot"
    >
      <div class="sidebar__divider" />
      <div
        class="sidebar__user"
        data-testid="session-user"
      >
        <span class="sidebar__name">{{ user.displayName }}</span>
        <span class="sidebar__email">{{ user.email }}</span>
      </div>
      <!-- Plain anchor: the authority ends the session. -->
      <a
        class="sidebar__signout"
        data-testid="sign-out-link"
        :href="signOutHref"
      >Sign out</a>
    </footer>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 16rem;
  flex: none;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--sidebar);
  color: var(--sidebar-foreground);
}

.sidebar__logo {
  display: flex;
  justify-content: center;
  padding: 1.25rem 1rem;
}

.sidebar__logo img {
  width: 10rem;
  object-fit: contain;
}

.sidebar__context {
  margin: 0;
  padding: 0 1.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.sidebar__divider {
  height: 1px;
  margin: 0 1.25rem;
  background: var(--sidebar-border);
}

.sidebar__body {
  flex: 1;
}

.sidebar__foot {
  padding: 1rem 0.75rem 1.25rem;
}

.sidebar__user {
  display: grid;
  gap: 0.125rem;
  margin-top: 1rem;
  padding: 0.625rem 0.75rem;
  border-radius: var(--radius);
  background: var(--sidebar-accent);
  color: var(--sidebar-accent-foreground);
  line-height: 1.25;
}

.sidebar__name {
  font-size: 0.875rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar__email {
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar__signout {
  display: inline-block;
  margin: 0.625rem 0.75rem 0;
  color: inherit;
  font-size: 0.875rem;
  font-weight: 600;
}

@media (max-width: 48rem) {
  .sidebar {
    width: 100%;
    min-height: 0;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  }

  .sidebar__context,
  .sidebar__body,
  .sidebar__divider {
    display: none;
  }

  .sidebar__foot {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-left: auto;
  }

  .sidebar__user {
    margin-top: 0;
  }
}
</style>
